import './AttachmentsList.scss';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Trans } from 'react-i18next';
import { RenameAttachmentModalContainer } from '../../../../modules/attachments/rename/ui/modal/RenameAttachmentModal.container';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { ConfirmationModalWithTextBox } from '../../../components/ConfirmationModalWithTextBox';
import { DecryptionScriptSampleContainer } from '../../../containers/DecryptionScriptSample.container';
import { ListViewMode } from '../../user/user.store';
import { AttachmentType, IAttachment } from '../attachments.store';
import { AttachmentAddFolderContainer } from '../containers/AttachmentAddFolder.container';
import { AttachmentConfirmDeleteModalContainer } from '../containers/AttachmentConfirmDeleteModal.container';
import { AttachmentDeleteContainer } from '../containers/AttachmentDelete.container';
import { AttachmentDownloadContainer } from '../containers/AttachmentDownload.container';
import { AttachmentSelectAllContainer } from '../containers/AttachmentSelectAll.container';
import { AttachmentsListControlsContainer } from '../containers/AttachmentsListControls.container';
import { AttachmentUploadContainer } from '../containers/AttachmentUpload.container';
import { AttachmentsBreadcrumbs, IAttachmentsBreadcrumbsProps } from './AttachmentsBreadcrumbs';
import { AttachmentsListTable, IAttachmentsListTableProps } from './AttachmentsListTable';
import { AttachmentsListThumbs, IAttachmentsListThumbsProps } from './AttachmentsListThumbs';

interface IProps extends IAttachmentsBreadcrumbsProps, IAttachmentsListTableProps, IAttachmentsListThumbsProps {
  attachmentsListViewMode: ListViewMode;
  addFolderEnabled: boolean;
  onDeleteCanceled: () => void;
  onDeleteConfirmed: () => Promise<void>;
  isDeleteConfirmationModalOpen: boolean;
  attachmentToDelete?: IAttachment;
}

export const AttachmentsList = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  const confirmationFolderDeleteQuestion = (
    <Trans
      i18nKey='delete_attachment_confirmation_header'
      values={{ name: props.attachmentToDelete?.name || '' }}
      components={{ b: <b className='persist-reduntant-spaces' /> }}
    />
  );

  return (
    <div className='attachment-list'>
      <AttachmentConfirmDeleteModalContainer />
      <div className='attachments-controls'>
        <div className='control-group'>
          {props.addFolderEnabled && <AttachmentAddFolderContainer />}
          <AttachmentUploadContainer />
          <div className='download-controls'>
            <DecryptionScriptSampleContainer />
            <AttachmentDownloadContainer />
          </div>
          <AttachmentDeleteContainer />
        </div>
        <div className='control-group'>
          <AttachmentSelectAllContainer />
          <AttachmentsListControlsContainer />
        </div>
      </div>
      <AttachmentsBreadcrumbs
        breadcrumbs={props.breadcrumbs}
        onFolderSelect={props.onFolderSelect}
      />
      {props.attachmentsListViewMode === ListViewMode.List ? (
        <AttachmentsListTable
          parentId={props.parentId}
          attachments={props.attachments}
          currentDownloadAttachments={props.currentDownloadAttachments}
          isLoading={props.isLoading}
          onFolderSelect={props.onFolderSelect}
          onDownloadAttachments={props.onDownloadAttachments}
          onDeleted={props.onDeleted}
          onOrderingChange={props.onOrderingChange}
          onPaginationChange={props.onPaginationChange}
          onSelectAttachment={props.onSelectAttachment}
          onSelectAllAttachments={props.onSelectAllAttachments}
          workspaceId={props.workspaceId}
          pagination={props.pagination}
          sorting={props.sorting}
          selectedAttachments={props.selectedAttachments}
        />
      ) : (
        <AttachmentsListThumbs
          workspaceId={props.workspaceId}
          attachments={props.attachments}
          authToken={props.authToken}
          isLoading={props.isLoading}
          selectedAttachments={props.selectedAttachments}
          pagination={props.pagination}
          onPaginationChange={props.onPaginationChange}
          onThumbCheck={props.onThumbCheck}
          onFileDownload={props.onFileDownload}
          onThumbDelete={props.onThumbDelete}
          getThumbnailUrl={props.getThumbnailUrl}
        />
      )}
      <ConfirmationModal
        showModal={props.isDeleteConfirmationModalOpen && props.attachmentToDelete?.type !== AttachmentType.FOLDER}
        confirmationHeader='delete_attachment_header'
        confirmationQuestion='delete_attachment_text_single'
        onCancel={props.onDeleteCanceled}
        onConfirm={props.onDeleteConfirmed}
        confirmationYes={'delete'}
      />
      <ConfirmationModalWithTextBox
        showModal={props.isDeleteConfirmationModalOpen && props.attachmentToDelete?.type === AttachmentType.FOLDER}
        confirmationHeader={t('delete_attachment_header')}
        confirmationQuestion={t('delete_attachment_text_single')}
        confirmationTextHeader={confirmationFolderDeleteQuestion}
        confirmationText={props.attachmentToDelete?.name || ''}
        onCancel={props.onDeleteCanceled}
        onConfirm={props.onDeleteConfirmed}
        confirmationYes={t('delete')}
      />
      <RenameAttachmentModalContainer />
    </div>
  );
});
